import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { can } from '../../shared/schema.js'
import { authApi } from './api.js'

/**
 * Session state and permission checks for the admin console.
 *
 * The session itself is an httpOnly cookie the server sets; this module only
 * caches *who* that cookie belongs to so the UI can render the right chrome.
 * It never holds a credential, so there is nothing here for an XSS to steal.
 *
 * Layer rule: logic only — decides what a user *may* do, never how it looks.
 *
 * 后台的会话状态与权限判断。
 *
 * 会话本身是服务端下发的 httpOnly cookie；本模块只缓存这个 cookie「属于谁」，
 * 供 UI 渲染对应界面。它不持有任何凭据，XSS 在这里偷不到东西。
 *
 * 分层规则：仅逻辑——只决定用户「能做什么」，不决定长什么样。
 */

const AuthContext = createContext(null)

/**
 * Provide session state to the admin tree.
 *
 * 向后台组件树提供会话状态。
 *
 * @param {{children: React.ReactNode}} props Children / 子节点。
 * @returns {JSX.Element} Provider / 上下文提供者。
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  // 'loading' until the first /auth/me settles. Routes must wait for this,
  // otherwise a signed-in user gets bounced to the login page on every reload.
  // 在首次 /auth/me 返回前为 loading。路由必须等它——否则已登录用户每次刷新都会被踢回登录页。
  const [status, setStatus] = useState('loading')
  // The host to offer when the console was opened over plain HTTP, or null.
  //
  // The server refuses every credential-bearing call on a cleartext connection,
  // so this probe is the first thing to hit that wall — before any password is
  // typed. Recording *why* it failed is the whole point: without it the console
  // knows only "not signed in", the login form reports "sign-in failed", and the
  // real cause (wrong scheme) never reaches the person who can fix it in one
  // click. That is not hypothetical; it is what the form did.
  //
  // 当后台是经明文 HTTP 打开时，这里存放可供跳转的主机名，否则为 null。
  //
  // 服务端会拒绝明文连接上的一切携带凭据的调用，因此这次探测是第一个撞到这堵墙的
  // ——发生在任何人输入密码之前。记录「为什么失败」正是要点：不记的话，
  // 后台只知道「未登录」，登录表单只会说「登录失败」，而真正的原因（协议用错了）
  // 永远传不到那个一次点击就能解决它的人手里。这不是假设，那正是该表单此前的行为。
  const [httpsHost, setHttpsHost] = useState(null)

  const refresh = useCallback(async () => {
    try {
      const { user: current } = await authApi.me()
      setUser(current || null)
      setStatus(current ? 'authenticated' : 'anonymous')
      setHttpsHost(null)
      return current || null
    } catch (error) {
      if (error?.status === 403 && error?.payload?.code === 'https_required') {
        setHttpsHost(error.payload.canonicalHost || null)
      }
      setUser(null)
      setStatus('anonymous')
      return null
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const login = useCallback(async (username, password) => {
    const { user: current } = await authApi.login(username, password)
    setUser(current)
    setStatus('authenticated')
    return current
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      // Clear locally even if the request failed — the user asked to leave.
      // 即使请求失败也本地清除——用户已经表达了退出意图。
      setUser(null)
      setStatus('anonymous')
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      isAdmin: user?.role === 'admin',
      // Non-null only when the console is being used over plain HTTP. The UI
      // reads it to say so plainly and offer the move, rather than letting the
      // failure surface as a wrong password.
      // 仅当后台正经明文 HTTP 使用时非 null。UI 据此把话说明白并提议跳转，
      // 而不是让这次失败以「密码错误」的面目出现。
      httpsHost,
      login,
      logout,
      refresh,
      /**
       * Permission gate, delegating to the shared table.
       *
       * 权限判断，委托给共享权限表。
       *
       * @param {string} area content|media|site|users / 权限域。
       * @param {string} action e.g. 'update' / 动作。
       * @returns {boolean} Allowed / 是否允许。
       */
      can: (area, action) => can(user?.role, area, action),
    }),
    [user, status, httpsHost, login, logout, refresh]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Access session state and permission checks.
 *
 * 获取会话状态与权限判断。
 *
 * @returns {object} Auth helpers / 认证工具集。
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
